package com.xtream.data_kmp.db.dao

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import com.xtream.data_kmp.WatchedLiveStreams
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class WatchedLiveDao_Impl(
  __db: RoomDatabase,
) : WatchedLiveDao {
  private val __db: RoomDatabase

  private val __insertAdapterOfWatchedLiveStreams: EntityInsertAdapter<WatchedLiveStreams>

  private val __deleteAdapterOfWatchedLiveStreams: EntityDeleteOrUpdateAdapter<WatchedLiveStreams>
  init {
    this.__db = __db
    this.__insertAdapterOfWatchedLiveStreams = object : EntityInsertAdapter<WatchedLiveStreams>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `watchedLiveStreamsTable` (`streamId`,`added`) VALUES (?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: WatchedLiveStreams) {
        statement.bindLong(1, entity.streamId.toLong())
        statement.bindLong(2, entity.added)
      }
    }
    this.__deleteAdapterOfWatchedLiveStreams = object : EntityDeleteOrUpdateAdapter<WatchedLiveStreams>() {
      protected override fun createQuery(): String = "DELETE FROM `watchedLiveStreamsTable` WHERE `streamId` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: WatchedLiveStreams) {
        statement.bindLong(1, entity.streamId.toLong())
      }
    }
  }

  public override suspend fun insertAll(vodStreams: List<WatchedLiveStreams>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfWatchedLiveStreams.insert(_connection, vodStreams)
  }

  public override suspend fun insert(watchedStream: WatchedLiveStreams): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfWatchedLiveStreams.insert(_connection, watchedStream)
  }

  public override suspend fun delete(watched: WatchedLiveStreams): Unit = performSuspending(__db, false, true) { _connection ->
    __deleteAdapterOfWatchedLiveStreams.handle(_connection, watched)
  }

  public override suspend fun deleteAll(watchedList: List<WatchedLiveStreams>): Unit = performSuspending(__db, false, true) { _connection ->
    __deleteAdapterOfWatchedLiveStreams.handleMultiple(_connection, watchedList)
  }

  public override suspend fun getSingle(streamId: Int?): WatchedLiveStreams? {
    val _sql: String = "SELECT * FROM watchedLiveStreamsTable WHERE (streamId = ?) LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (streamId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, streamId.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _result: WatchedLiveStreams?
        if (_stmt.step()) {
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: Long
          _tmpAdded = _stmt.getLong(_columnIndexOfAdded)
          _result = WatchedLiveStreams(_tmpStreamId,_tmpAdded)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getSingleFlow(streamId: Int?): Flow<WatchedLiveStreams?> {
    val _sql: String = "SELECT * FROM watchedLiveStreamsTable WHERE (streamId = ?) LIMIT 1"
    return createFlow(__db, false, arrayOf("watchedLiveStreamsTable")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (streamId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, streamId.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _result: WatchedLiveStreams?
        if (_stmt.step()) {
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: Long
          _tmpAdded = _stmt.getLong(_columnIndexOfAdded)
          _result = WatchedLiveStreams(_tmpStreamId,_tmpAdded)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getSizeAsFlow(): Flow<Long?> {
    val _sql: String = "SELECT COUNT(*) FROM watchedLiveStreamsTable"
    return createFlow(__db, false, arrayOf("watchedLiveStreamsTable")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long?
        if (_stmt.step()) {
          val _tmp: Long?
          if (_stmt.isNull(0)) {
            _tmp = null
          } else {
            _tmp = _stmt.getLong(0)
          }
          _result = _tmp
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getSize(): Long? {
    val _sql: String = "SELECT COUNT(*) FROM watchedLiveStreamsTable"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long?
        if (_stmt.step()) {
          val _tmp: Long?
          if (_stmt.isNull(0)) {
            _tmp = null
          } else {
            _tmp = _stmt.getLong(0)
          }
          _result = _tmp
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun delete() {
    val _sql: String = "DELETE FROM watchedLiveStreamsTable"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteById(streamId: Int) {
    val _sql: String = "DELETE FROM watchedLiveStreamsTable WHERE streamId = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, streamId.toLong())
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteByOffset(offset: Int, limit: Int) {
    val _sql: String = """
        |
        |       DELETE FROM watchedLiveStreamsTable
        |        WHERE streamId in 
        |(SELECT streamId FROM watchedLiveStreamsTable ORDER BY added DESC  LIMIT ?  OFFSET ?)
        |   
        """.trimMargin()
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, limit.toLong())
        _argIndex = 2
        _stmt.bindLong(_argIndex, offset.toLong())
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
